import type { Point } from './math';

/**
 * Infantry squad formation shapes (see INSTRUCTION.md section 11).
 * Each maps to deterministic member-slot offsets around the squad anchor.
 */
export type Formation = 'loose' | 'line' | 'compact' | 'garrison' | 'retreat';

/** Morale state derived from suppression (see section 12.3). */
export type MoraleState = 'normal' | 'suppressed' | 'pinned' | 'retreating';

/**
 * Deterministic member-slot offsets for a given formation and member count.
 * Derived purely from entity id + member index, never from Math.random.
 * Slots are local-space; the renderer adds the anchor position and facing.
 */
export const formationSlots = (
  formation: Formation,
  memberCount: number,
  entityId: number,
): Point[] => {
  const slots: Point[] = [];
  const jitter = (index: number): number => {
    // Stable pseudo-offset in [-1, 1] from entity id + index, no RNG state.
    const hash = Math.imul((entityId + 1) * 1009 + index * 9176, 2654435761);
    return ((hash >>> 16) / 32768 - 1) * 0.18;
  };
  for (let index = 0; index < memberCount; index++) {
    slots.push(slotOffset(formation, index, memberCount, jitter(index)));
  }
  return slots;
};

const slotOffset = (formation: Formation, index: number, total: number, noise: number): Point => {
  switch (formation) {
    case 'garrison':
      // Hidden inside a building; spread subtly so silhouettes don't perfectly overlap.
      return { x: ((index % 3) - 1) * 7 + noise * 8, y: (Math.floor(index / 3) - 0.5) * 7 };
    case 'retreat':
      // Column toward friendly lines.
      return { x: noise * 6, y: -index * 11 };
    case 'line': {
      const half = (total - 1) / 2;
      return { x: (index - half) * 16 + noise * 4, y: noise * 6 };
    }
    case 'compact': {
      // Tight cover formation.
      const cols = Math.ceil(Math.sqrt(total));
      return {
        x: ((index % cols) - (cols - 1) / 2) * 10 + noise * 3,
        y: (Math.floor(index / cols) - (total - 1) / (2 * cols)) * 10,
      };
    }
    case 'loose':
    default: {
      // Loose movement: staggered two-row wedge with deterministic offsets.
      const row = Math.floor(index / 3);
      return { x: ((index % 3) - 1) * 14 + noise * 10, y: row * 13 - 6 + noise * 5 };
    }
  }
};

/** Maps a suppression value to a morale state per section 12.3 thresholds. */
export const moraleFromSuppression = (suppression: number, retreating: boolean): MoraleState => {
  if (retreating) return 'retreating';
  if (suppression >= 100) return 'pinned';
  if (suppression >= 60) return 'suppressed';
  return 'normal';
};

/**
 * Current formation for a squad given its state.
 * Pinned squads retain their last formation but cannot move.
 */
export const activeFormation = (formation: Formation, morale: MoraleState): Formation =>
  morale === 'retreating' ? 'retreat' : formation;

/**
 * Per-member health record. A compact, deterministic representation of the
 * "per-member health data" requirement. Special-weapon members occupy higher
 * indices so ordinary members are depleted first.
 */
export interface MemberHealth {
  /** Full HP fraction of each surviving member (0..1), indexed by surviving slot. */
  fractions: number[];
  /** Index of the special-weapon member, if any (preserved until practical). */
  specialistIndex: number | undefined;
}
